import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthCtx } from '../App';
import { toast } from 'react-toastify';
import PasswordInput from '../components/ui/PasswordInput';

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';
  const [form, setForm] = useState({
    firstName: '', lastName: '', dateOfBirth: '', email: '', password: '', referralCode: refCode,
  });
  const [errors, setErrors]   = useState({});
  const { register, login, loading } = useAuthCtx();
  const navigate = useNavigate();

  const computeAge = (isoDate) => {
    const dob = new Date(isoDate);
    if (Number.isNaN(dob.getTime())) return NaN;
    const now = new Date();
    let age = now.getUTCFullYear() - dob.getUTCFullYear();
    const m = now.getUTCMonth() - dob.getUTCMonth();
    if (m < 0 || (m === 0 && now.getUTCDate() < dob.getUTCDate())) age--;
    return age;
  };

  /** Client-side validation */
  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim())  e.lastName  = 'Required';
    if (!form.dateOfBirth) {
      e.dateOfBirth = 'Required';
    } else {
      const age = computeAge(form.dateOfBirth);
      if (Number.isNaN(age))      e.dateOfBirth = 'Invalid date';
      else if (age < 21)          e.dateOfBirth = 'You must be 21 or older to create an account';
      else if (age > 120)         e.dateOfBirth = 'Please enter a valid date of birth';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email required';
    if (form.password.length < 8)          e.password = 'At least 8 characters';
    if (!/[A-Z]/.test(form.password))      e.password = 'Must include uppercase letter';
    if (!/[0-9]/.test(form.password))      e.password = 'Must include a number';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    try {
      await register(form);
      await login(form.email, form.password);
      toast.success('Welcome to AB8 Beauty!');
      navigate('/dashboard');
    } catch (err) {
      if (err.response?.data?.fields) {
        const fe = {};
        err.response.data.fields.forEach(f => { fe[f.field] = f.message; });
        setErrors(fe);
      } else {
        toast.error(err.response?.data?.error || 'Registration failed');
      }
    }
  };

  const set = (f) => (e) => setForm(prev => ({ ...prev, [f]: e.target.value }));

  // Latest DOB that still makes the user 21+ today — used as the date picker's max.
  const maxDob = (() => {
    const d = new Date();
    d.setUTCFullYear(d.getUTCFullYear() - 21);
    return d.toISOString().slice(0, 10);
  })();

  return (
    <div className="min-h-screen bg-tea-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full card">
        <div className="text-center mb-8">
          <Link to="/" aria-label="AB8 Beauty home" className="inline-flex items-center justify-center">
            <img src="/logo-ab8.svg" alt="AB8 Beauty &amp; Skin" className="h-20 w-auto" />
          </Link>
          <h1 className="mt-4 text-2xl font-bold">Create your account</h1>
          <p className="text-xs text-tea-800 bg-tea-50 border border-tea-200 rounded-md p-2 mt-3 leading-relaxed">
            Join AB8 Beauty to shop clean, glow-forward skincare — and earn referral
            rewards when you share the products you love.
          </p>
          {refCode && (
            <p className="text-tea-600 text-sm mt-1">
              Referred by code: <strong>{refCode}</strong>
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* First / Last name row */}
          <div className="grid grid-cols-2 gap-4">
            {[['firstName', 'First Name'], ['lastName', 'Last Name']].map(([f, label]) => (
              <div key={f}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                <input value={form[f]} onChange={set(f)} className="input-field" placeholder={label} />
                {errors[f] && <p className="text-red-500 text-xs mt-1">{errors[f]}</p>}
              </div>
            ))}
          </div>

          {/* Date of birth — 21+ age gate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth
            </label>
            <input
              type="date"
              value={form.dateOfBirth}
              onChange={set('dateOfBirth')}
              max={maxDob}
              className="input-field"
            />
            <p className="text-xs text-gray-500 mt-1">You must be 21 or older to create an account.</p>
            {errors.dateOfBirth && <p className="text-red-500 text-xs mt-1">{errors.dateOfBirth}</p>}
          </div>

          {/* Email + Password */}
          {[
            ['email',    'Email',    'email',    'you@example.com'],
            ['password', 'Password', 'password', 'Min 8 chars, 1 uppercase, 1 number'],
          ].map(([f, label, type, ph]) => (
            <div key={f}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
              {type === 'password' ? (
                <PasswordInput value={form[f]} onChange={set(f)} placeholder={ph} />
              ) : (
                <input type={type} value={form[f]} onChange={set(f)} className="input-field" placeholder={ph} />
              )}
              {errors[f] && <p className="text-red-500 text-xs mt-1">{errors[f]}</p>}
            </div>
          ))}

          {/* Referral code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Referral Code (optional)
            </label>
            <input value={form.referralCode} onChange={set('referralCode')}
              className="input-field" placeholder="e.g. ALICE2026" />
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full py-3">
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p className="text-center mt-6 text-gray-600 text-sm">
          Already have an account?{' '}
          <Link to="/login" className="text-tea-600 font-medium hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
